import React, { useEffect, useState } from "react";
import { apiGetJson, apiPatchJson } from "../utils/apiClient";
import { isStandardEquivalentPlan } from "../utils/plan";

// ============================================================
// プラン別ガイドURL
// ============================================================
const GUIDE_URLS: Record<string, string> = {
  free_ecx:     "https://ec-vision.co.jp/rieki-navi/setup/ecx_free/",
  light_ecx:    "https://ec-vision.co.jp/rieki-navi/setup/ecx_light/",
  standard_ecx: "https://ec-vision.co.jp/rieki-navi/setup/ecx_standard/",
  standard:     "https://ec-vision.co.jp/rieki-navi/setup/standard/",
};

const MEETING_URL = "https://timerex.net/s/r.ono_4d3f/9250cdde";

/** ECX判定: tenant/provider/token のいずれかが ECX を示せば ECX 経由とみなす */
function isEcxUser(): boolean {
  if (typeof window === "undefined") return false;
  const tenant = String(localStorage.getItem("selectedTenant") || sessionStorage.getItem("login_tenant") || "").toLowerCase();
  const provider = String(localStorage.getItem("auth_provider") || "").toLowerCase();
  const token = String(localStorage.getItem("ecx_access_token") || "").trim();
  return tenant === "ecx" || provider === "ecx" || token.length > 0;
}

/** プランとtenantIdに応じたガイドURLを返す */
export function getGuideUrl(plan: string | null | undefined, tenantId?: string | null): string {
  const p = String(plan ?? "").toLowerCase();
  const t = String(tenantId ?? "").toLowerCase();
  const isEcxPlan = p.includes("ecx");
  const isEcxTenant = t === "ecx";
  if (isStandardEquivalentPlan(p)) {
    return isEcxPlan || isEcxTenant || isEcxUser() ? GUIDE_URLS.standard_ecx : GUIDE_URLS.standard;
  }
  if (p.includes("ライト") || p.includes("light")) {
    return GUIDE_URLS.light_ecx;
  }
  return GUIDE_URLS.free_ecx;
}

async function fetchUiFlags(): Promise<Record<string, boolean>> {
  try {
    const r = await apiGetJson("/api/me/ui-flags");
    return (r as any)?.flags ?? {};
  } catch {
    return {};
  }
}

type BannerVisibility = "loading" | "visible" | "dismissed";

function toBase64Url(value: string): string {
  if (typeof window === "undefined") return value;
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function getSetupBannerFlagKey(companyId: string | null | undefined): string | null {
  const id = String(companyId ?? "").trim();
  if (!id) return null;
  return `setup_banner_dismissed_${toBase64Url(id)}`;
}

function getZoomBannerFlagKey(companyId: string | null | undefined): string | null {
  const id = String(companyId ?? "").trim();
  if (!id) return null;
  return `zoom_banner_dismissed_${toBase64Url(id)}`;
}

async function saveUiFlag(key: string, value: boolean): Promise<boolean> {
  try {
    await apiPatchJson("/api/me/ui-flags", { flags: { [key]: value } });
    return true;
  } catch (e) {
    console.warn("[SetupBanners] failed to save flag to Firestore", key, e);
    return false;
  }
}

// ============================================================
// コンポーネント A: ダッシュボード上部バナー（全プラン）
// ============================================================
export function SetupBanner({ plan, tenantId, companyId }: { plan: string | null | undefined; tenantId?: string | null; companyId?: string | null }) {
  const flagKey = getSetupBannerFlagKey(companyId);

  const [visibility, setVisibility] = useState<BannerVisibility>("loading");

  useEffect(() => {
    let cancelled = false;

    if (!flagKey) {
      setVisibility("loading");
      return () => {
        cancelled = true;
      };
    }

    setVisibility("loading");

    fetchUiFlags().then((flags) => {
      if (cancelled) return;
      setVisibility(flags[flagKey] ? "dismissed" : "visible");
    });

    return () => {
      cancelled = true;
    };
  }, [flagKey]);

  if (!flagKey || visibility !== "visible") return null;

  const guideUrl = getGuideUrl(plan, tenantId);

  const handleDismiss = async () => {
    setVisibility("dismissed");
    const ok = await saveUiFlag(flagKey, true);
    if (!ok) setVisibility("visible");
  };

  return (
    <div
      style={{
        background: "#EEEDFE",
        border: "0.5px solid #AFA9EC",
        borderLeft: "4px solid #534AB7",
        borderRadius: 8,
        padding: "1rem 1.25rem",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "1rem",
        marginBottom: "1rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: ".75rem", flex: 1 }}>
        <div
          style={{
            width: 32,
            height: 32,
            minWidth: 32,
            background: "#534AB7",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="8" cy="8" r="6" />
            <line x1="8" y1="5" x2="8" y2="8" />
            <circle cx="8" cy="11" r=".5" fill="#fff" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#26215C", marginBottom: ".25rem" }}>
            はじめに初期設定をお願いします
          </div>
          <div style={{ fontSize: 13, color: "#534AB7", lineHeight: 1.6, marginBottom: ".625rem" }}>
            RMSスタッフ追加・受注データダウンロード用ID、パスワードの登録・商品原価登録・固定費の設定を完了すると、正確な利益データが表示されます。
            <br />
            <span style={{ fontSize: 12, color: "#7F77DD" }}>
              ✓ 設定が完了したら、右の × を押してこの表示を閉じてください
            </span>
          </div>
          <a
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "#534AB7",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              padding: ".5rem 1rem",
              borderRadius: 6,
              textDecoration: "none",
            }}
          >
            初期設定ガイドを見る →
          </a>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        title="設定が完了したら閉じてください"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#888780",
          fontSize: 20,
          lineHeight: 1,
          flexShrink: 0,
          padding: 0,
          marginTop: -2,
        }}
        aria-label="閉じる"
      >
        ×
      </button>
    </div>
  );
}

// ============================================================
// コンポーネント C: オンライン面談誘導バナー（スタンダード相当プランのみ）
// ============================================================
export function ZoomBanner({ plan, companyId }: { plan: string | null | undefined; companyId?: string | null }) {
  const isStandard = isStandardEquivalentPlan(plan);
  const flagKey = getZoomBannerFlagKey(companyId);

  const [visibility, setVisibility] = useState<BannerVisibility>("loading");

  useEffect(() => {
    let cancelled = false;

    if (!isStandard || !flagKey) {
      setVisibility("loading");
      return () => {
        cancelled = true;
      };
    }

    setVisibility("loading");

    fetchUiFlags().then((flags) => {
      if (cancelled) return;
      setVisibility(flags[flagKey] ? "dismissed" : "visible");
    });

    return () => {
      cancelled = true;
    };
  }, [isStandard, flagKey]);

  if (!isStandard || !flagKey || visibility !== "visible") return null;

  const handleDismiss = async () => {
    setVisibility("dismissed");
    const ok = await saveUiFlag(flagKey, true);
    if (!ok) setVisibility("visible");
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "2px solid #534AB7",
        borderRadius: 12,
        padding: "1.5rem",
        position: "relative",
        marginBottom: "1rem",
      }}
    >
      <button
        onClick={handleDismiss}
        style={{
          position: "absolute",
          top: ".875rem",
          right: ".875rem",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#888780",
          fontSize: 20,
          lineHeight: 1,
          padding: 0,
        }}
        aria-label="閉じる"
      >
        ×
      </button>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: "#534AB7",
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
          padding: "3px 12px",
          borderRadius: 20,
          marginBottom: ".75rem",
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        >
          <polygon points="1 3 1 9 8 9 8 3" />
          <polyline points="8 5 11 3 11 9 8 7" />
        </svg>
        スタンダードプラン限定
      </div>

      <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a18", marginBottom: ".375rem", lineHeight: 1.4 }}>
        初期設定の完了後、オンライン面談をご予約ください
      </div>

      <div style={{ fontSize: 13, color: "#5F5E5A", lineHeight: 1.7, marginBottom: "1rem" }}>
        利益Naviの数値が揃ったタイミングで、代表の小野と30分のオンライン面談を実施しています。無料ですのでお気軽にご予約ください。
      </div>

      {MEETING_URL ? (
        <a
          href={MEETING_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#534AB7",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            padding: ".75rem 1.5rem",
            borderRadius: 8,
            textDecoration: "none",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <polygon points="1 3 1 9 8 9 8 3" />
            <polyline points="8 4.5 13 3 13 9 8 7.5" />
          </svg>
          オンライン面談を予約する
        </a>
      ) : (
        <div style={{ fontSize: 12, color: "#888780" }}>
          ※ 予約URLは準備中です。担当者までお問い合わせください。
        </div>
      )}
    </div>
  );
}
